import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { ethers } from "ethers";
import { Button } from "@/components/ui/Button";
import { usePolygonClient } from "@/hooks/usePolygonClient";
import { useWalletBalances } from "@/hooks/useWalletBalances";
import { getTokenConfigs } from "@/config/tokens";

interface SendTokenFormProps {
  /** Label shown above the form */
  title?: string;
}

type Status = "idle" | "sending" | "success" | "error";

const ERC20_TRANSFER_ABI = ["function transfer(address to, uint256 amount) returns (bool)"];

function SendTokenForm({ title }: SendTokenFormProps) {
  const { t } = useTranslation();
  const polygonClient = usePolygonClient();
  const { data: balances, refetch: refetchBalances } = useWalletBalances();

  const token = "USDT" as const;
  const [toAddress, setToAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  const tokenConfigs = getTokenConfigs();
  const tokenConfig = tokenConfigs.get(token);
  const balance = balances?.usdt;

  const isValidAddress = ethers.isAddress(toAddress);
  const numAmount = Number(amount);
  const canSend =
    status === "idle" &&
    isValidAddress &&
    numAmount > 0 &&
    balance !== undefined &&
    numAmount <= balance &&
    polygonClient !== null;

  const handleSend = async () => {
    if (!canSend || !polygonClient || !tokenConfig) return;

    setStatus("sending");
    setError(null);

    try {
      const tokenContract = new ethers.Contract(
        tokenConfig.address,
        ERC20_TRANSFER_ABI,
        polygonClient.signer,
      );
      const decimals = tokenConfig.decimals;
      const rawAmount = ethers.parseUnits(amount, decimals);

      const tx = await (tokenContract as ethers.Contract & { transfer: (to: string, amount: bigint) => Promise<ethers.TransactionResponse> }).transfer(toAddress, rawAmount);
      await tx.wait();

      setStatus("success");
      refetchBalances();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleReset = () => {
    setStatus("idle");
    setToAddress("");
    setAmount("");
    setError(null);
  };

  if (status === "success") {
    return (
      <div className="rounded-xl border border-success-200 bg-success-50 p-4 dark:border-success-700 dark:bg-success-900/20">
        <div className="flex items-start gap-2.5">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-success-600 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-success-800 dark:text-success-200">
              {t("ramp.sendSuccess", "Tokens sent successfully!")}
            </p>
            <p className="text-xs text-success-600 dark:text-success-300">
              {amount} {token} → {toAddress.slice(0, 6)}...{toAddress.slice(-4)}
            </p>
            <Button variant="ghost" size="sm" onClick={handleReset} className="mt-2">
              {t("ramp.sendAnother", "Send another")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-brand-200 bg-brand-50/50 p-4 dark:border-brand-800 dark:bg-brand-900/20">
      {title && (
        <p className="text-sm font-medium text-text-primary">{title}</p>
      )}

      {/* Destination address */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-muted">
          {t("ramp.destinationAddress", "Destination address (Polygon)")}
        </label>
        <input
          type="text"
          value={toAddress}
          onChange={(e) => setToAddress(e.target.value.trim())}
          placeholder="0x..."
          className="w-full rounded-lg border border-border-default bg-surface px-3 py-2 font-mono text-xs text-text-primary placeholder:text-text-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>

      {/* Amount */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-medium text-text-muted">
            {t("ramp.amount", "Amount")}
          </label>
          {balance !== undefined && (
            <button
              type="button"
              onClick={() => setAmount(balance.toString())}
              className="text-xs text-brand-500 hover:text-brand-600"
            >
              Max: {balance.toFixed(2)}
            </button>
          )}
        </div>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          min="0"
          step="0.01"
          className="w-full rounded-lg border border-border-default bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      </div>

      {/* Error */}
      {status === "error" && error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 dark:border-red-800 dark:bg-red-900/20">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-500 mt-0.5" />
          <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Send button */}
      <Button
        onClick={handleSend}
        disabled={!canSend}
        className="w-full gap-2"
        size="sm"
      >
        {status === "sending" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("ramp.sending", "Sending...")}
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            {t("ramp.sendTokens", "Send {{token}}", { token })}
          </>
        )}
      </Button>
    </div>
  );
}

export { SendTokenForm };
